/** Cliente de la API de plataforma (`{cloud}/platform`). */

import { useSession } from "@/stores/session.ts";

export const API_BASE_URL =
  (import.meta.env.VITE_CLOUD_API_URL as string | undefined) ?? "http://localhost:8000";

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** FastAPI devuelve `{detail: string}` o `{detail: [{msg}]}` (validación). */
function errorMessage(body: unknown, fallback: string): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => (d as { msg?: string }).msg ?? "").join(" · ");
  }
  return fallback;
}

export async function request<T>(
  method: "GET" | "POST" | "PATCH",
  path: string,
  body?: unknown,
): Promise<T> {
  const { token, clear } = useSession.getState();
  const response = await fetch(`${API_BASE_URL}/platform${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    // Token vencido o revocado: volver al login.
    if (response.status === 401 && token) clear();
    throw new ApiError(response.status, errorMessage(data, `Error ${response.status}`));
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
};
